import { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Line,
} from "recharts";
import {
  getFiscalData,
  getFiscalStockPrice,
} from "../../redux/actions/CompanyAction";
import { RadioSelector } from "../../utils/Constants/systemSetting";
import { Tooltip } from "antd";
import PuffLoader from "react-spinners/PuffLoader";

export default function DailyPricesGraph({ companyInfo, loadingStockPrices }) {
  const dispatch = useDispatch();

  const { financialData } = useSelector((state) => state.CompanyReducer);
  const { stockPrice } = useSelector((state) => state.CompanyReducer);

  const getFormattedDate = (daysAgo = 0, monthsAgo = 0, yearsAgo = 0) => {
    const pad = (n) => String(n).padStart(2, "0");
    const date = new Date();

    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    // Set to first of current month, then go back one month
    const lastMonth = new Date(year - yearsAgo, month - monthsAgo, 1);

    // Get max valid day in that month
    const lastDay = new Date(
      lastMonth.getFullYear(),
      lastMonth.getMonth() + 1,
      0,
    ).getDate();

    // Use the smaller of original day or last valid day
    const targetDay = Math.min(day, lastDay);

    const revisedDate = new Date(
      lastMonth.getFullYear(),
      lastMonth.getMonth(),
      targetDay,
    );

    revisedDate.setDate(revisedDate.getDate() - daysAgo); // move back daysAgo days

    const revisedYear = revisedDate.getFullYear();
    const revisedMonth = pad(revisedDate.getMonth() + 1);
    const revisedDay = pad(revisedDate.getDate());

    const formatted = `${revisedYear}-${revisedMonth}-${revisedDay}`;
    return formatted;
  };

  const timerangeOptions = useMemo(() => {
    return [
      {
        title: "7d",
        startDate: getFormattedDate(7),
        endDate: getFormattedDate(),
      },
      {
        title: "30d",
        startDate: getFormattedDate(0, 1),
        endDate: getFormattedDate(),
      },
      {
        title: "3m",
        startDate: getFormattedDate(0, 3),
        endDate: getFormattedDate(),
      },
      {
        title: "6m",
        startDate: getFormattedDate(0, 6),
        endDate: getFormattedDate(),
      },
      {
        title: "1y",
        startDate: getFormattedDate(0, 0, 1),
        endDate: getFormattedDate(),
      },
      {
        title: "2y",
        startDate: getFormattedDate(0, 0, 2),
        endDate: getFormattedDate(),
      },
      {
        title: "3y",
        startDate: getFormattedDate(0, 0, 3),
        endDate: getFormattedDate(),
      },
      {
        title: "5y",
        startDate: getFormattedDate(0, 0, 5),
        endDate: getFormattedDate(),
      },
      {
        title: "10y",
        startDate: getFormattedDate(0, 0, 10),
        endDate: getFormattedDate(),
      },
    ];
  }, []);

  const timerangeOptionTitles = timerangeOptions.map((item) => item.title);

  const [timerangeOptionSelected, setTimerangeOptionSelected] = useState("1y");
  const timerangeOptionDetails = timerangeOptions.find(
    (item) => item.title === timerangeOptionSelected,
  );

  const [updateNeeded, setUpdateNeeded] = useState(false);
  const lastFetchedRef = useRef(Date.now());

  useEffect(() => {
    const checkIfUpdateNeeded = () => {
      const now = Date.now();
      setUpdateNeeded(now - lastFetchedRef.current > 60000); // refresh every minute
    };

    checkIfUpdateNeeded(); // initial check
    const interval = setInterval(checkIfUpdateNeeded, 10000); // check every 10s

    return () => clearInterval(interval);
  }, []);

  const [currentStockPriceDetails, setCurrentStockPriceDetails] = useState({});

  const currentStockPrice = useMemo(() => {
    const currentStockPrice =
      stockPrice?.[companyInfo.Ticker]?.result?.[0]?.ratio; //TODO: ?.ratio
    //TODO: Need to check loading state to actually get this working since the set state happens before the next loop
    if (updateNeeded) {
      return "Updating...";
    }

    if (!isNaN(parseFloat(currentStockPrice))) {
      setCurrentStockPriceDetails(
        stockPrice?.[companyInfo.Ticker]?.result?.[0],
      );

      return parseFloat(currentStockPrice).toFixed(2);
    }
    if (stockPrice?.[companyInfo.Ticker]) {
      return "N/A";
    }
    return null;
  }, [companyInfo, stockPrice, updateNeeded]);

  useEffect(() => {
    const token = localStorage.getItem("ACCESS_TOKEN");
    const ticker = companyInfo?.Ticker;
    const CIK = companyInfo?.CIK;

    if (ticker && (!stockPrice?.[ticker] || updateNeeded)) {
      lastFetchedRef.current = Date.now();
      dispatch(
        getFiscalStockPrice(
          "Current Stock Price",
          [ticker],
          [CIK], //CIKs
          null, //periodType
          null,
          "USD",
          true, //Pulls current stock price only "latest" = true
          token,
        ),
      );
      setUpdateNeeded(false);
    }
    // if (ticker && !financialData?.["Current Stock Price"]?.[ticker]) {
    //   dispatch(
    //     getFiscalData(
    //       "Current Stock Price",
    //       [ticker],
    //       [CIK], //CIKs
    //       null, //periodType
    //       null, //periodsNeeded
    //       "USD",
    //       false,
    //       token
    //     )
    //   );
    // }
  }, [companyInfo, financialData, stockPrice, updateNeeded, dispatch]);

  const [rawStockPrices, setRawStockPrices] = useState([]);
  useEffect(() => {
    const companyStockPrices =
      financialData?.["Current Stock Price"]?.[companyInfo?.Ticker]?.result;
    if (Array.isArray(companyStockPrices)) {
      const refreshedStockPrices = [
        ...companyStockPrices.slice(0),
        // currentStockPriceDetails,
      ];
      setRawStockPrices(refreshedStockPrices);
    }
  }, [companyInfo, financialData, currentStockPriceDetails]);

  function normalizeToMidnight(date) {
    const d = new Date(date);
    d.setHours(23, 59, 0, 0);
    return d.getTime();
  }

  // Turn date strings into timestamps (for numeric x-axis)
  const processedStockPrices = typeof Array.isArray(rawStockPrices)
    ? rawStockPrices
        .map((d) => ({ ...d, ts: normalizeToMidnight(d.date) }))
        .sort((a, b) => a.ts - b.ts)
    : []; // Make sure it's sorted

  function filterDataByDateRange(data, startDate, endDate) {
    const start = normalizeToMidnight(new Date(startDate));
    const end = normalizeToMidnight(new Date(endDate));

    return data.filter((d) => {
      const ts = normalizeToMidnight(new Date(d.date));
      return ts >= start && ts <= end;
    });
  }
  const filteredStockPrices = filterDataByDateRange(
    processedStockPrices,
    timerangeOptionDetails.startDate,
    timerangeOptionDetails.endDate,
  );

  function CustomTooltip({ active, payload }) {
    if (!active || !payload || !payload.length) return null;

    //TODO: Replace the 3 "ratio" points with "price" if using "Stock Prices" endpoint
    const { date, ratio } = payload[0].payload;

    return (
      <div
        style={{
          backgroundColor: "#fff",
          border: "1px solid #ccc",
          padding: "8px",
          fontSize: "0.875rem",
        }}
      >
        <div>
          <strong>
            Date:{" "}
            {new Date(date).toLocaleDateString("en-US", {
              timeZone: "UTC",
              month: "2-digit",
              day: "2-digit",
              year: "2-digit",
            })}
          </strong>
        </div>
        <div>Price: {ratio ? `$${ratio.toFixed(2)}` : ""}</div>
      </div>
    );
  }

  const tsMin = Math.min(...filteredStockPrices.map((d) => d.ts));
  const tsMax = Math.max(...filteredStockPrices.map((d) => d.ts));
  function formatEpochTo12Hour(epoch) {
    const date = new Date(epoch);
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12;
    hours = hours ? hours : 12; // 0 becomes 12

    return `${hours}:${minutes}:${seconds} ${ampm}`;
  }

  // useMemo(() => {
  //   console.log(financialData, stockPrice);
  // }, [financialData, stockPrice]);

  return (
    <div style={{ marginTop: -15 }}>
      <div>
        <span style={{ fontSize: 18, marginLeft: 65 }}>
          Latest Price:{" "}
          {currentStockPrice ? `$${currentStockPrice}` : "loading..."}{" "}
          {currentStockPrice ? (
            <Tooltip title="Reflects last time stock price was checked for an update. Due to data provider limitations, stock price may be up to 15 minutes delayed.">
              <span style={{ fontSize: 12, cursor: "help" }}>
                (as of {formatEpochTo12Hour(lastFetchedRef.current)})
              </span>
            </Tooltip>
          ) : (
            ""
          )}
        </span>
      </div>
      <RadioSelector
        options={timerangeOptionTitles}
        state={timerangeOptionSelected}
        setState={setTimerangeOptionSelected}
        heading={"Select Time Range:"}
        style={{ marginLeft: 65, marginBottom: 15 }}
      />
      <ResponsiveContainer width="100%" height={300}>
        {loadingStockPrices ? (
          <center
            style={{
              height: "150px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PuffLoader
              color={"var(--loader)"}
              loading={loadingStockPrices}
              size={75}
            />
          </center>
        ) : !filteredStockPrices.length ? (
          <center
            style={{
              height: "150px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <p style={{ fontSize: 18 }}>
              Stock Price history is not available for this time range.
            </p>
          </center>
        ) : (
          <ScatterChart>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="ts"
              type="number"
              domain={[tsMin, tsMax]}
              // tickFormatter={(ts) => new Date(ts).toISOString().slice(5, 10)} // MM-DD
              tickFormatter={(ts) =>
                new Date(ts).toLocaleDateString("en-US", {
                  timeZone: "UTC",
                  month: "2-digit",
                  day: "2-digit",
                  year: "2-digit",
                })
              }
              name="Date"
              label={{
                value: "Date",
                position: "insideBottom",
                offset: -5,
              }}
            />
            <YAxis
              dataKey="ratio" //TODO: "price" if using "Stock Prices"
              domain={["auto", "auto"]}
              name="Price"
              label={{
                value: "Price (USD)",
                angle: -90,
                position: "insideLeft",
                offset: 10,
              }}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Scatter
              data={filteredStockPrices}
              line={{ stroke: "var(--secondary)", strokeWidth: 2 }}
              shape={(props) => (
                <circle
                  cx={props.cx}
                  cy={props.cy}
                  r={6}
                  fill="transparent"
                  stroke="transparent"
                  strokeWidth={0}
                />
              )}
            />
          </ScatterChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
